import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { SG_FETCH_PUBLIC_UPLOAD } from "constants/actions";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "../components/ui/table";
import { Alert, AlertDescription } from "../components/ui/alert";
import { ArrowLeft, Edit, Download, FileText, Info, File, Clock, Code, Loader2, AlertTriangle, Lock, CheckCircle, Key, Copy } from "lucide-react";

export default function UploadDetail() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { publicUploadDetail, loading } = useSelector(
    (state) => state.public_upload
  );

  useEffect(() => {
    if (id) {
      dispatch({ type: SG_FETCH_PUBLIC_UPLOAD, payload: { id } });
    }
  }, [id]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getStatusVariant = (status) => {
    return status === 1 ? "default" : "secondary";
  };

  const makeSpaceSafeUrl = (url) => (typeof url === "string" ? encodeURI(url) : url);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="mt-4 text-sm text-muted-foreground">Loading upload details...</p>
        </CardContent>
      </Card>
    );
  }

  if (!publicUploadDetail) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>Upload not found</AlertDescription>
      </Alert>
    );
  }

  const isImageFile = () => {
    const mime = publicUploadDetail?.mime_type || "";
    const url = publicUploadDetail?.file_url || "";
    if (mime.startsWith("image/")) return true;
    return /\.(png|jpe?g|gif|webp|svg)$/i.test(url);
  };

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <FileText className="h-6 w-6" />
                Upload Details
              </CardTitle>
              <CardDescription>ID: {publicUploadDetail.id}</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate("/app/public-uploads")}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to List
              </Button>
              <Button variant="outline" onClick={() => navigate(`/app/public-uploads/upload/edit/${id}`)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Metadata
              </Button>
              <Button
                asChild
              >
                <a
                  href={makeSpaceSafeUrl(publicUploadDetail.file_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download File
                </a>
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Info className="h-5 w-5" />
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="font-semibold w-1/3">Name</TableCell>
                  <TableCell>{publicUploadDetail.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold">Description</TableCell>
                  <TableCell>
                    {publicUploadDetail.description || (
                      <span className="text-muted-foreground italic">No description</span>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold">Category</TableCell>
                  <TableCell>
                    <Badge variant="outline">{publicUploadDetail.category_text}</Badge>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold">Status</TableCell>
                  <TableCell>
                    <Badge variant={getStatusVariant(publicUploadDetail.status)}>
                      {publicUploadDetail.status_text}
                    </Badge>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold">Public Access</TableCell>
                  <TableCell>
                    {publicUploadDetail.is_public ? (
                      <Badge className="gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Public
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1">
                        <Lock className="h-3 w-3" />
                        Private
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="font-semibold w-1/3">File Type</TableCell>
                  <TableCell>
                    {publicUploadDetail.file_type || publicUploadDetail.mime_type}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold">MIME Type</TableCell>
                  <TableCell>
                    <code className="text-sm bg-muted px-2 py-1 rounded">
                      {publicUploadDetail.mime_type}
                    </code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold">File Size</TableCell>
                  <TableCell>{formatFileSize(publicUploadDetail.file_size)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold">Organization</TableCell>
                  <TableCell>
                    {publicUploadDetail.organization_name || (
                      <Badge variant="outline">Global</Badge>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold">Sort Order</TableCell>
                  <TableCell>{publicUploadDetail.sort_order}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* File Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <File className="h-5 w-5" />
            File Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell className="font-semibold w-1/4">File Path</TableCell>
                <TableCell>
                  <code className="text-sm bg-muted px-2 py-1 rounded block">
                    {publicUploadDetail.file_path}
                  </code>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-semibold">File URL</TableCell>
                <TableCell>
                  <a
                    href={makeSpaceSafeUrl(publicUploadDetail.file_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1 break-all"
                  >
                    {makeSpaceSafeUrl(publicUploadDetail.file_url)}
                    <Download className="h-3 w-3 flex-shrink-0" />
                  </a>
                </TableCell>
              </TableRow>
              {!publicUploadDetail.is_public && publicUploadDetail.access_key && (
                <TableRow>
                  <TableCell className="font-semibold">Access Key</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="text-sm bg-muted px-2 py-1 rounded break-all">
                        {publicUploadDetail.access_key}
                      </code>
                      <button
                        className="text-xs text-primary inline-flex items-center gap-1"
                        onClick={() => navigator.clipboard.writeText(publicUploadDetail.access_key)}
                        title="Copy access key"
                      >
                        <Copy className="h-3 w-3" /> Copy
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Image Preview */}
      {isImageFile() && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full overflow-auto">
              <img
                src={publicUploadDetail.file_url}
                alt={publicUploadDetail.name || "Image preview"}
                className="max-h-[480px] rounded border border-muted"
                loading="lazy"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metadata */}
      {publicUploadDetail.metadata &&
        Object.keys(publicUploadDetail.metadata).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Code className="h-5 w-5" />
                Metadata
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-muted p-4 rounded-lg overflow-auto text-sm">
                {JSON.stringify(publicUploadDetail.metadata, null, 2)}
              </pre>
            </CardContent>
          </Card>
        )}

      {/* Timestamps */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Timestamps
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-1">Created</p>
              <p className="text-lg">
                {format(
                  new Date(publicUploadDetail.created_at),
                  "MMMM dd, yyyy 'at' HH:mm:ss"
                )}
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-1">Last Updated</p>
              <p className="text-lg">
                {format(
                  new Date(publicUploadDetail.updated_at),
                  "MMMM dd, yyyy 'at' HH:mm:ss"
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
